import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ClipboardList, 
  Brain, 
  BookOpen, 
  MessageCircle, 
  Target, 
  AlertTriangle,
  FileText,
  Sparkles,
  Shield,
  TrendingUp,
  Milestone,
  UserCog,
  GraduationCap
} from "lucide-react";
import { ClinicalAssessments } from "./ClinicalAssessments";
import { ClientHomework } from "./ClientHomework";
import { CaseFormulation } from "./CaseFormulation";
import { SupervisionNotes } from "./SupervisionNotes";
import { RiskAlerts } from "./RiskAlerts";
import { ClientJourneyMilestones } from "./ClientJourneyMilestones";
import { SessionPrepBrief } from "./SessionPrepBrief";
import { VisualProgressReport } from "./VisualProgressReport";
import { ResourceLibrary } from "./ResourceLibrary";
import { CheckInPrompts } from "./CheckInPrompts";
import { AISessionNotes } from "./AISessionNotes";
import { GDPRDataExport } from "./GDPRDataExport";
import { ClinicianAIAssistant } from "./ClinicianAIAssistant";

interface ClinicalToolsHubProps {
  clinicianId: string;
  clients: any[];
  sessions: any[];
}

export function ClinicalToolsHub({ clinicianId, clients, sessions }: ClinicalToolsHubProps) {
  const [selectedClientId, setSelectedClientId] = useState<string>(
    clients.length > 0 ? clients[0].client_id : ""
  );
  const [activeTab, setActiveTab] = useState("assessments");

  const toolCategories = [
    {
      id: "outcome",
      label: "Outcome Tracking",
      tools: [
        { id: "assessments", label: "Assessments", icon: ClipboardList },
        { id: "progress", label: "Progress Reports", icon: TrendingUp },
        { id: "milestones", label: "Milestones", icon: Milestone }
      ]
    },
    {
      id: "ai",
      label: "AI Support",
      tools: [
        { id: "tpp-qsp", label: "TPP/QSP Consultant", icon: GraduationCap },
        { id: "prep", label: "Session Prep", icon: Brain },
        { id: "notes", label: "AI Notes", icon: Sparkles },
        { id: "alerts", label: "Risk Alerts", icon: AlertTriangle }
      ]
    },
    {
      id: "engagement",
      label: "Client Engagement",
      tools: [
        { id: "homework", label: "Homework", icon: BookOpen },
        { id: "resources", label: "Resources", icon: FileText },
        { id: "checkins", label: "Check-Ins", icon: MessageCircle }
      ]
    },
    {
      id: "professional",
      label: "Professional",
      tools: [
        { id: "formulation", label: "Formulation", icon: Target },
        { id: "supervision", label: "Supervision", icon: UserCog },
        { id: "gdpr", label: "GDPR Export", icon: Shield }
      ]
    }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "assessments":
        return <ClinicalAssessments clientId={selectedClientId} clinicianId={clinicianId} />;
      case "progress":
        return <VisualProgressReport clientId={selectedClientId} clinicianId={clinicianId} />;
      case "milestones":
        return <ClientJourneyMilestones clientId={selectedClientId} clinicianId={clinicianId} />;
      case "tpp-qsp":
        return <ClinicianAIAssistant />;
      case "prep":
        return <SessionPrepBrief clientId={selectedClientId} clinicianId={clinicianId} />;
      case "notes":
        return <AISessionNotes clinicianId={clinicianId} sessions={sessions} />;
      case "alerts":
        return <RiskAlerts clinicianId={clinicianId} />;
      case "homework":
        return <ClientHomework clientId={selectedClientId} clinicianId={clinicianId} />;
      case "resources":
        return <ResourceLibrary clinicianId={clinicianId} clients={clients} />;
      case "checkins":
        return <CheckInPrompts clinicianId={clinicianId} clients={clients} />;
      case "formulation":
        return <CaseFormulation clientId={selectedClientId} clinicianId={clinicianId} />;
      case "supervision":
        return <SupervisionNotes clinicianId={clinicianId} />;
      case "gdpr":
        return <GDPRDataExport clinicianId={clinicianId} clients={clients} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Client Selector for client-specific tools */}
      {["assessments", "progress", "milestones", "prep", "homework", "formulation"].includes(activeTab) && (
        <Card className="bg-card/50 border-border/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">Working with:</span>
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="flex-1 max-w-xs px-3 py-2 rounded-lg bg-background border border-border text-foreground"
              >
                {clients.length === 0 ? (
                  <option value="">No clients available</option>
                ) : (
                  clients.map(client => (
                    <option key={client.id} value={client.client_id}>
                      {client.client_profile?.display_name || 'Client'}
                    </option>
                  ))
                )}
              </select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tool Categories */}
      <div className="grid lg:grid-cols-4 gap-4">
        {toolCategories.map(category => (
          <Card key={category.id} className="bg-card/50 border-border/30">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-muted-foreground mb-3">{category.label}</h3>
              <div className="space-y-1">
                {category.tools.map(tool => {
                  const Icon = tool.icon;
                  const isActive = activeTab === tool.id;
                  return (
                    <button
                      key={tool.id}
                      onClick={() => setActiveTab(tool.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                        isActive 
                          ? "bg-primary text-primary-foreground" 
                          : "hover:bg-muted/50 text-foreground"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tool.label}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Content Area */}
      <div className="min-h-[400px]">
        {clients.length === 0 && ["assessments", "progress", "milestones", "prep", "homework", "formulation"].includes(activeTab) ? (
          <Card className="bg-card/50 border-border/30">
            <CardContent className="py-12 text-center">
              <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Clients Yet</h3>
              <p className="text-muted-foreground">
                Add clients to your practice to use this feature
              </p>
            </CardContent>
          </Card>
        ) : (
          renderContent()
        )}
      </div>
    </div>
  );
}
